
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TimeSlot } from '@/types';
import { useBooking } from "@/contexts/BookingContext";
import ScheduleHeader from "@/components/scheduling/ScheduleHeader";
import ScheduleCalendar from "@/components/scheduling/ScheduleCalendar";
import TimeSlotSelector from "@/components/scheduling/TimeSlotSelector";
import BookingSummary from "@/components/scheduling/BookingSummary";
import { useBookingService } from "@/hooks/use-booking-service";
import { toast } from "sonner";

const SchedulePage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { bookingDetails, setBookingDetails } = useBooking();
  const { createBooking } = useBookingService();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to booking page if no booking details exist
    if (!bookingDetails) {
      navigate('/booking');
    }
  }, [bookingDetails, navigate]);

  const handleConfirm = async () => {
    if (!bookingDetails || !selectedDate || !selectedTimeSlot) return;
    
    setIsSubmitting(true);
    
    // Update booking details with date and time
    const updatedBookingDetails = {
      ...bookingDetails,
      date: selectedDate,
      timeSlot: selectedTimeSlot
    };
    
    try {
      // Update context
      setBookingDetails(updatedBookingDetails);
      
      // Create the booking in the database
      const result = await createBooking(updatedBookingDetails);
      
      if (result.success) {
        toast("Booking Confirmed", {
          description: "Your plant care service has been scheduled."
        });
        
        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        throw new Error("Failed to create booking");
      }
    } catch (error) {
      console.error('Error confirming booking:', error);
      toast("Booking Failed", {
        description: "There was an error scheduling your service. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!bookingDetails) {
    return null;
  }

  // Get zone from booking details if available
  const zone = bookingDetails.zone;

  return (
    <div className="min-h-screen bg-white">
      <ScheduleHeader bookingDetails={bookingDetails} />
      
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-front-gray-900 mb-3">Select Date</h2>
              <p className="font-cousine text-front-gray-600 text-sm mb-6">
                Choose your preferred service date
              </p>
            </div>
            <ScheduleCalendar 
              selectedDate={selectedDate}
              onSelectDate={(date) => {
                setSelectedDate(date);
                setSelectedTimeSlot(undefined); // Reset time slot when date changes
              }}
              zone={zone}
            />
          </div>
          
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-front-gray-900 mb-3">Select Time</h2>
              <p className="font-cousine text-front-gray-600 text-sm mb-6">
                Choose your preferred time slot
              </p>
            </div>
            <TimeSlotSelector
              selectedDate={selectedDate}
              selectedTimeSlot={selectedTimeSlot}
              onSelectTimeSlot={setSelectedTimeSlot}
              zone={zone}
            />
            
            {selectedDate && selectedTimeSlot && (
              <div className="mt-8">
                <BookingSummary
                  bookingDetails={bookingDetails}
                  selectedDate={selectedDate}
                  selectedTimeSlot={selectedTimeSlot}
                  onConfirm={handleConfirm}
                  isLoading={isSubmitting}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulePage;
